<template>
    <common-modal v-if="isOpen" width="base" :closeOnClickAway="closeOnClickAway" @close="emitEvent({ name: 'close' })">
        <common-content-spacer height="medium">
            <common-page-header
                :heading="$t('pages.auth.verify_code.heading')"
                :content="$t('pages.auth.verify_code.content', { email })"
                :isCenter="true"
                contentSpacing="medium"
                contentColor="secondary"
            ></common-page-header>
            <common-content-wrapper width="small">
                <form-wrapper class="form__fields" :isSubmittable="false">
                    <div class="code-input-container">
                        <input
                            v-for="(digit, index) in codeDigits"
                            :key="index"
                            :ref="`digit${index}`"
                            v-model="codeDigits[index]"
                            type="text"
                            maxlength="1"
                            class="code-digit-input"
                            :class="{ 'has-error': errorMessage }"
                            @input="onDigitInput(index, $event)"
                            @keydown="onKeyDown(index, $event)"
                            @paste="onPaste"
                        />
                    </div>

                    <div v-if="errorMessage" class="error-message">
                        {{ errorMessage }}
                    </div>

                    <common-buttons class="modal-buttons">
                        <common-button
                            :label="$t('pages.auth.verify_code.form.cancel')"
                            type="secondary"
                            size="small"
                            :isProcessing="isLoading"
                            @click="emitEvent({ name: 'close' })"
                        ></common-button>
                        <common-button
                            :label="$t('pages.auth.verify_code.form.verify_code')"
                            type="primary"
                            size="small"
                            :isProcessing="isLoading"
                            :isDisabled="!isCodeComplete"
                            @click="verifyCode"
                        ></common-button>
                    </common-buttons>
                </form-wrapper>
            </common-content-wrapper>
        </common-content-spacer>
    </common-modal>
</template>

<script>
    import ReferralsPoolAPI from 'api/ReferralsPoolAPI'

    export default {
        props: {
            isOpen: { type: Boolean, required: true },
            email: { type: String, required: true },
            accessLogId: { type: Number, required: false, default: null },
            closeOnClickAway: { type: Boolean, default: true },
        },

        data() {
            return {
                isLoading: false,
                errorMessage: '',
                codeDigits: ['', '', '', '', '', ''],
            }
        },

        computed: {
            isCodeComplete() {
                return this.codeDigits.every((digit) => digit !== '')
            },
        },

        methods: {
            focusFirstInput() {
                const firstInput = this.$refs.digit0
                if (firstInput && firstInput[0]) {
                    firstInput[0].focus()
                }
            },

            onDigitInput(index, event) {
                const value = event.target.value
                this.errorMessage = ''

                if (value && /^[a-zA-Z\d]$/.test(value)) {
                    this.codeDigits[index] = value

                    if (index < 5) {
                        const nextInput = this.$refs[`digit${index + 1}`]
                        if (nextInput && nextInput[0]) {
                            nextInput[0].focus()
                        }
                    }
                } else {
                    this.codeDigits[index] = ''
                }
            },

            onKeyDown(index, event) {
                if (event.key === 'Backspace' && !this.codeDigits[index] && index > 0) {
                    const prevInput = this.$refs[`digit${index - 1}`]
                    if (prevInput && prevInput[0]) {
                        prevInput[0].focus()
                    }
                } else if (event.key === 'ArrowLeft' && index > 0) {
                    const prevInput = this.$refs[`digit${index - 1}`]
                    if (prevInput && prevInput[0]) {
                        prevInput[0].focus()
                    }
                } else if (event.key === 'ArrowRight' && index < 5) {
                    const nextInput = this.$refs[`digit${index + 1}`]
                    if (nextInput && nextInput[0]) {
                        nextInput[0].focus()
                    }
                }
            },

            onPaste(event) {
                event.preventDefault()
                const pastedData = event.clipboardData.getData('text').trim()

                if (/^[a-zA-Z\d]{6}$/.test(pastedData)) {
                    pastedData.split('').forEach((digit, index) => {
                        this.$set(this.codeDigits, index, digit)
                    })
                    this.$nextTick(() => {
                        const lastInput = this.$refs.digit5
                        if (lastInput && lastInput[0]) {
                            lastInput[0].focus()
                        }
                    })
                }
            },

            async verifyCode() {
                if (!this.isCodeComplete) return
                const code = this.codeDigits.join('')

                this.errorMessage = ''
                this.isLoading = true

                ReferralsPoolAPI.verifyWithToken(code, this.accessLogId)
                    .then((referral) => {
                        this.emitEvent({ name: 'code-verified', payload: referral })
                    })
                    .catch(() => {
                        this.errorMessage = 'Invalid verification code. Please try again.'
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            },
        },

        watch: {
            isOpen(newVal) {
                if (newVal) {
                    this.$nextTick(() => {
                        this.focusFirstInput()
                    })
                }
            },
        },

        mounted() {
            if (this.isOpen) {
                this.$nextTick(() => {
                    this.focusFirstInput()
                })
            }
        },
    }
</script>

<style scoped lang="scss">
    .code-input-container {
        display: flex;
        gap: 10px;
        justify-content: center;
        margin: 30px 0;
    }

    .code-digit-input {
        width: 48px;
        height: 56px;
        text-align: center;
        font-size: 24px;
        font-weight: 600;
        border: 2px solid #e0e0e0;
        border-radius: 8px;
        outline: none;
        transition: all 0.2s ease;

        &:focus {
            border-color: #4a90e2;
            box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
        }

        &.has-error {
            border-color: #e74c3c;
        }
    }

    .error-message {
        color: #e74c3c;
        font-size: 14px;
        text-align: center;
        margin-top: -15px;
        margin-bottom: 15px;
    }

    .modal-buttons {
        margin-top: 20px;
        display: flex;
        gap: 10px;
        justify-content: flex-end;
    }
</style>
